import { Link, NavLink } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  Bell,
  ClipboardList,
  Cpu,
  CreditCard,
  LayoutDashboard,
  MapPinned,
  MessageSquare,
  MessageSquareWarning,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFeedback } from "@/lib/queries";

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Overview", icon: <LayoutDashboard className="h-4 w-4" />, end: true },
  { to: "/users", label: "Users", icon: <Users className="h-4 w-4" /> },
  { to: "/map", label: "Map", icon: <MapPinned className="h-4 w-4" /> },
  { to: "/errors", label: "Errors", icon: <AlertCircle className="h-4 w-4" /> },
  { to: "/activity", label: "Activity", icon: <Activity className="h-4 w-4" /> },
  { to: "/messages", label: "Messages", icon: <MessageSquare className="h-4 w-4" /> },
  { to: "/feedback", label: "Feedback", icon: <MessageSquareWarning className="h-4 w-4" /> },
  { to: "/subscriptions", label: "Subscriptions", icon: <CreditCard className="h-4 w-4" /> },
  { to: "/notifications", label: "Notifications", icon: <Bell className="h-4 w-4" /> },
  { to: "/system", label: "System", icon: <Cpu className="h-4 w-4" /> },
  { to: "/audit", label: "Audit log", icon: <ClipboardList className="h-4 w-4" /> },
];

/** The "BP" brand, linking home. Shared by the desktop sidebar and the mobile menu. */
export function SidebarBrand({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="flex h-14 items-center gap-2 border-b border-border/60 px-4">
      <Link
        to="/"
        onClick={onNavigate}
        aria-label="BillyPocket admin overview"
        className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="grid h-7 w-7 place-items-center rounded-md bg-foreground text-[11px] font-bold tracking-tighter text-background">
          BP
        </span>
        <div>
          <div className="text-sm font-semibold leading-none">BillyPocket</div>
          <div className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">admin</div>
        </div>
      </Link>
    </div>
  );
}

/** The main navigation. React Query dedupes the feedback count when two copies render. */
export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const feedbackSummary = useFeedback(
    { limit: 1, offset: 0, status: "all" },
    { refetchInterval: 30_000 }
  );
  const openFeedbackCount = feedbackSummary.data?.summary.open;

  return (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 p-2">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
              isActive
                ? "bg-secondary text-foreground"
                : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
            )
          }
        >
          {item.icon}
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.to === "/feedback" && openFeedbackCount != null ? (
            <span
              aria-label={`${openFeedbackCount.toLocaleString()} open feedback reports`}
              className={cn(
                "ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold leading-none tabular-nums",
                openFeedbackCount > 0
                  ? "bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/25"
                  : "bg-secondary text-muted-foreground ring-1 ring-border/60"
              )}
            >
              {openFeedbackCount.toLocaleString()}
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden w-56 shrink-0 flex-col border-r border-border/60 bg-card/30 lg:flex">
      <SidebarBrand />
      <SidebarNav />
      <div className="border-t border-border/60 p-3 text-[10px] uppercase tracking-widest text-muted-foreground">
        god mode
      </div>
    </aside>
  );
}
